import { observable, makeObservable, action } from 'mobx';
import i18next from 'i18next';
import { IProps as IToastProps } from '../components/Toast';

const TOAST_TIMEOUT = 5 * 1000;

export enum Preset {
  Primary = 'primary',
  Secondary = 'secondary',
  Danger = 'danger'
}

interface IToastCodeProps {
  code: string;
  preset?: Preset;
}

export interface IToastModelProps {
  id?: string;
  text: string;
  preset?: Preset;
}

function toastCodeToLangCode(code: string) {
  const strings = code.split('_');
  let langCode = strings[0];

  for (let i = 1; i < strings.length; i += 1) {
    const string = strings[i];
    langCode = `${langCode}${string[0].toUpperCase()}${string.substring(1)}`;
  }

  return langCode;
}

class Toasts {
  public toasts: IToastProps[] = [];
  // this attribute keep tracks of toasts that we received on page load
  // it is useful for application to know if the page load was a redirection from a previous action
  public history: IToastProps[] = [];
  private timeoutIds: number[] = [];

  constructor() {
    makeObservable(this, {
      toasts: observable,
      add: action,
      multiAdd: action,
      addCode: action,
      multiAddCode: action,
      reset: action
    });

    this.reset();
  }

  public add(newToast: IToastModelProps) {
    if (!newToast || newToast.text === '') return;

    const existingToastIndex = this.existingToastIndex(newToast);
    if (existingToastIndex !== -1) this.remove(existingToastIndex);

    const toastProps = {
      ...newToast,
      id: newToast.id || this.generateId()
    };

    this.toasts.unshift(toastProps);
    this.history.unshift(toastProps);

    const timeoutId = window.setTimeout(() => this.remove(), TOAST_TIMEOUT);
    this.timeoutIds.unshift(timeoutId);
  }

  public multiAdd(toasts: IToastModelProps[] = []) {
    if (!Array.isArray(toasts)) return;

    for (const toast of toasts) {
      this.add(toast);
    }
  }

  public addCode(newToast: IToastCodeProps) {
    if (!newToast) return;

    const langCode = toastCodeToLangCode(newToast.code);
    const text = i18next.t(`Toast.${langCode}`);

    const toastData = {
      text,
      id: this.generateId(),
      preset: newToast.preset
    };
    const existingToastIndex = this.existingToastIndex(toastData);
    if (existingToastIndex !== -1) this.remove(existingToastIndex);

    this.toasts.unshift(toastData);
    this.history.unshift(toastData);

    const timeoutId = window.setTimeout(() => this.remove(), TOAST_TIMEOUT);
    this.timeoutIds.unshift(timeoutId);
  }

  public multiAddCode(toasts: IToastCodeProps[] = []) {
    if (!Array.isArray(toasts)) return;

    for (const toast of toasts) this.addCode(toast);
  }

  public reset() {
    this.toasts = [];
  }

  private generateId(): string {
    return (Date.now() + Math.random()).toString();
  }

  private remove(index: number | undefined = undefined) {
    const toasts = this.toasts;
    const timeoutIds = this.timeoutIds;

    if (index !== undefined && index >= 0 && index < toasts.length) {
      toasts.splice(index, 1);
      // Stop the old timeout dismissing the replacement toast
      window.clearTimeout(timeoutIds[index]);
      timeoutIds.splice(index, 1);
    } else if (toasts.length) {
      toasts.pop();
      // Keep timeoutIds aligned with toasts
      timeoutIds.pop();
    }
  }

  private existingToastIndex(newToast: IToastModelProps) {
    return this.toasts.findIndex(
      (t) =>
        t.id === newToast.id ||
        (t.text === newToast.text && t.preset === newToast.preset)
    );
  }
}

export default new Toasts();
